/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FC,
  MouseEvent,
  ReactNode,
  memo,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { ListboxContext } from './ListboxContext';
import { BoxProps } from '@apia/theme';
import { decodeHTMLEntities } from '@apia/util';

export type TListboxItem = {
  children?: ReactNode;
  rowIndex?: number;
} & BoxProps & {
    /**
     * El label se usa en aria-label por motivos de accesibilidad y además en
     * la funcionalidad de búsqueda.
     */
    label: string;
  };

export const ListboxItem: FC<TListboxItem> = memo(
  ({ children, rowIndex: outerRowIndex, label, ...domProps }) => {
    const {
      getState,
      listboxActions,
      onDoubleClickNode,
      onClickNode,
      useListbox,
    } = useContext(ListboxContext);
    const rowIndex = useMemo(() => outerRowIndex ?? -1, [outerRowIndex]);
    const ref = useListbox<HTMLLIElement>('row', { rowIndex });

    return (
      <>
        {domProps.className?.includes('separator') && (
          <li className="separator"></li>
        )}
        <li
          {...(domProps as any)}
          className={`${
            domProps.className
              ?.split(' ')
              .filter((current) => current !== 'separator')
              .join(' ') ?? ''
          } listbox__row`}
          ref={ref}
          role="option"
          onDoubleClick={useCallback(
            (ev: MouseEvent<HTMLElement>) => {
              ev.preventDefault();
              onDoubleClickNode?.({
                ...ev,
                target: (ev.target as HTMLElement).closest(
                  '[role="option"]',
                ) as HTMLElement,
              });
            },
            [onDoubleClickNode],
          )}
          onMouseDown={useCallback(
            (ev: MouseEvent<HTMLElement>) => {
              onClickNode?.({
                ...ev,
                target: (ev.target as HTMLElement).closest(
                  '[role="option"]',
                ) as HTMLElement,
              });
              if (
                (getState().isMultipleSelection && ev.shiftKey) ||
                ev.detail > 1
              )
                ev.preventDefault();
              listboxActions.updateFocusByClickEvent({
                rowIndex,
                isCtrlKey: ev.ctrlKey,
                isShiftKey: ev.shiftKey,
              });
            },
            [getState, listboxActions, onClickNode, rowIndex],
          )}
          aria-label={label}
          title={useMemo(() => decodeHTMLEntities(label), [label])}
        >
          {children}
        </li>
      </>
    );
  },
);

ListboxItem.displayName = 'ListboxItem';
